/**
 * Team workspace: "My teams" (every team you are in, or all of them for admins and the CEO, plus the business-unit
 * workspaces that used to live alone on /workspaces) and one page per team with its tasks, shared notes, shared
 * files, the lead's dashboard and the member list.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import clsx from "clsx";
import { AlertTriangle, Send, Building2, ChevronRight, Crown, Download, FileText, LayoutDashboard, ListChecks, NotebookPen, Paperclip, Plus, Upload, UserMinus, Users } from "lucide-react";
import { useRef, useState } from "react";
import { api, del, post, put } from "../lib/api";
import type { Person } from "../lib/work";
import { DueInput } from "../components/DueInput";
import { upload } from "../components/TaskFiles";
import { Due, PersonPicker, StatusPill } from "../components/work";
import { Avatar, Card, Empty, ErrorNote, Field, Modal, PageHeader, Stat, fmtDate } from "../components/ui";
import { useCan } from "../lib/session";
import { useOrg } from "./admin/organisation";

interface TeamCard { id: string; name: string; kind: string | null; company: string | null; leadName: string | null; members: number; isMember: boolean; isLead: boolean }
interface Member { id: string; name: string; email: string | null; role: string; lead: boolean }
interface TeamDetail {
  team: { id: string; name: string; kind: string | null; company: string | null; leadPersonId: string | null };
  members: Member[];
  space: { id: string; key: string } | null;
  can: { manage: boolean; dashboard: boolean };
  isMember: boolean;
}
interface TeamTask { id: string; ref: string; title: string; state: string; statusKey: string | null; priority: string; dueAt: string | null; assigneeIds: string[]; assigneeNames: string | null; ownerName: string | null; updatedAt: string }
interface TeamDoc { id: string; title: string; filename: string; contentType: string; size: number | null; addedAt: string; addedBy: string | null; fusionDocsUrl: string | null; fusionDocsStatus: string | null }
interface Dash {
  totals: { open: number; overdue: number; doneThisWeek: number; unassigned: number };
  people: Array<{ id: string; name: string; open: number; overdue: number; blocked: number; waiting: number; doneThisWeek: number }>;
  attention: Array<{ id: string; ref: string; title: string; state: string; statusKey: string | null; dueAt: string | null; assigneeNames: string | null }>;
}
interface TeamNote { id: string; title: string; snippet: string; ownerName: string | null; updatedAt: string; pinned: boolean }

// assignee names come back as one joined string from the API; accept an array too in case that changes
const names = (v: string | string[] | null | undefined) => (Array.isArray(v) ? v.join(", ") : v ?? "");
const human = (n?: number | null) => (n == null ? "" : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);

/* ───────────────────────── My teams ───────────────────────── */

/** New workspace: a team led by you, with its own tasks, notes and files. */
function NewWorkspaceDialog({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const org = useOrg();
  const [f, setF] = useState({ name: "", companyId: "" });
  const create = useMutation({
    mutationFn: () => post<{ id: string }>("/api/teams", f),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["teams-mine"] }); onClose(); void nav({ to: "/teams/$id", params: { id: r.id } }); },
  });
  return (
    <Modal title="New workspace" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={f.name.trim().length < 2 || !f.companyId || create.isPending} onClick={() => create.mutate()}>Create</button></>}>
      <ErrorNote error={create.error} />
      <div className="space-y-3">
        <Field label="Name"><input className="input" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Mythos Front Desk" /></Field>
        <Field label="Company">
          <select className="input" value={f.companyId} onChange={(e) => setF({ ...f, companyId: e.target.value })}>
            <option value="">Pick…</option>
            {(org.data?.companies ?? []).filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <p className="muted text-xs">You lead it. Add people or invite them, then share tasks, notes and files; you get the team dashboard.</p>
      </div>
    </Modal>
  );
}

/** Invite someone not yet in ONE straight into this team, as Staff, with the sign-in email. */
function InviteToTeamDialog({ teamId, teamName, onClose }: { teamId: string; teamName: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", email: "", note: "" });
  const invite = useMutation({
    mutationFn: () => post<{ emailed: boolean; emailError: string | null }>("/api/directory/invite", { name: f.name, email: f.email, note: f.note || undefined, template: "staff", teamIds: [teamId], sendEmail: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["team", teamId] }),
  });
  const valid = f.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim());
  if (invite.data) return (
    <Modal title="Invited" onClose={onClose} footer={<button className="btn btn-primary" onClick={onClose}>Done</button>}>
      <p className="text-sm">{f.name} is now in {teamName}.</p>
      {invite.data.emailed ? <p className="mt-2 text-sm text-emerald-700">The invitation email went to {f.email.trim()}.</p>
        : <p className="mt-2 text-sm text-amber-700">The email could not be sent ({invite.data.emailError}). Send them https://one.infinite-fusion.com and tell them to sign in with Microsoft using {f.email.trim()}.</p>}
    </Modal>
  );
  return (
    <Modal title={`Invite to ${teamName}`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!valid || invite.isPending} onClick={() => invite.mutate()}><Send size={15} /> {invite.isPending ? "Inviting…" : "Invite and email"}</button></>}>
      <ErrorNote error={invite.error} />
      <div className="space-y-3">
        <Field label="Full name"><input className="input" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Work email" hint="The Microsoft account they will sign in with."><input className="input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@infinite-fusion.com" /></Field>
        <Field label="Personal note (optional)"><textarea className="input" rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <p className="muted text-xs">They join as Staff: their own work, what's shared with them, and this team. If they're already in ONE, they're just added to the team.</p>
      </div>
    </Modal>
  );
}

export function TeamsPage() {
  const teams = useQuery({ queryKey: ["teams-mine"], queryFn: () => api<{ items: TeamCard[] }>("/api/teams/mine") });
  const can = useCan();
  const [creating, setCreating] = useState(false);
  const ws = useQuery({ queryKey: ["workspaces"], queryFn: () => api<{ items: Array<{ id: string; code: string; name: string; company: string }> }>("/api/workspaces") });
  const items = teams.data?.items ?? [];
  return (
    <div className="space-y-8">
      <PageHeader title="My teams" subtitle="Your teams' shared tasks, notes and files in one place."
        actions={(can("directory.team.create") || can("directory.team.manage")) && <button className="btn btn-primary" onClick={() => setCreating(true)}><Plus size={16} /> New workspace</button>} />
      {creating && <NewWorkspaceDialog onClose={() => setCreating(false)} />}
      <section>
        <ErrorNote error={teams.error} />
        {teams.isLoading ? <Empty>Loading…</Empty> : items.length ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((t) => (
              <Link key={t.id} to="/teams/$id" params={{ id: t.id }} className="panel group flex items-start gap-4 p-5 transition hover:-translate-y-px hover:border-brand-200 hover:shadow-md">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100"><Users size={22} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-bold group-hover:text-brand-700">{t.name}</span>
                  {t.company && <span className="muted block truncate text-sm">{t.company}</span>}
                  <span className="muted mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                    {t.leadName && <span className="inline-flex items-center gap-1"><Crown size={12} /> {t.leadName}</span>}
                    <span>{t.members} {t.members === 1 ? "member" : "members"}</span>
                  </span>
                  {(t.isLead || !t.isMember) && (
                    <span className="mt-2 flex flex-wrap gap-1.5">
                      {t.isLead && <span className="chip !border-brand-200 !bg-brand-50 !text-brand-700">You lead this team</span>}
                      {!t.isMember && <span className="chip">Not a member</span>}
                    </span>
                  )}
                </span>
                <ChevronRight size={18} className="subtle mt-3 shrink-0 transition group-hover:translate-x-0.5 group-hover:text-brand-600" />
              </Link>
            ))}
          </div>
        ) : !teams.error && <Card><Empty icon={<Users size={20} />}>You're not in a team yet. Ask your manager or an admin to add you.</Empty></Card>}
      </section>
      {(ws.data?.items.length ?? 0) > 0 && (
        <section>
          <h2 className="section-label mb-3">Business workspaces</h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {ws.data!.items.map((w) => (
              <Link key={w.id} to="/workspaces/$code" params={{ code: w.code }} className="panel group flex items-center gap-4 p-5 transition hover:-translate-y-px hover:border-brand-200 hover:shadow-md">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[var(--panel-2)] text-[var(--muted)] ring-1 ring-[var(--line)]"><Building2 size={22} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-bold group-hover:text-brand-700">{w.name}</span>
                  <span className="muted block truncate text-sm">{w.company}</span>
                </span>
                <ChevronRight size={18} className="subtle shrink-0 transition group-hover:translate-x-0.5 group-hover:text-brand-600" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ───────────────────────── One team ───────────────────────── */

type Tab = "tasks" | "notes" | "files" | "dashboard" | "members";

export function TeamPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery({ queryKey: ["team", id], queryFn: () => api<TeamDetail>(`/api/teams/${id}`) });
  const [tab, setTab] = useState<Tab>("tasks");
  if (!q.data) return q.isLoading ? <Empty>Loading…</Empty> : <ErrorNote error={q.error} />;
  const d = q.data;
  const lead = d.members.find((m) => m.lead);
  const tabs: Array<{ key: Tab; label: string; icon: typeof Users }> = [
    { key: "tasks", label: "Tasks", icon: ListChecks },
    { key: "notes", label: "Notes", icon: NotebookPen },
    { key: "files", label: "Files", icon: Paperclip },
    ...(d.can.dashboard ? [{ key: "dashboard" as Tab, label: "Dashboard", icon: LayoutDashboard }] : []),
    { key: "members", label: "Members", icon: Users },
  ];
  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={<Link to="/teams" className="hover:underline">My teams</Link>}
        title={d.team.name}
        subtitle={[d.team.company, lead ? `Led by ${lead.name}` : null, `${d.members.length} ${d.members.length === 1 ? "member" : "members"}`].filter(Boolean).join(" · ")}
      />
      <nav className="scroll-thin -mx-4 flex gap-1 overflow-x-auto border-b border-[var(--line)] px-4 sm:mx-0 sm:px-0" role="tablist">
        {tabs.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={clsx("-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-semibold", tab === t.key ? "border-brand-600 text-brand-700" : "border-transparent text-[var(--muted)] hover:text-[var(--text)]")}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </nav>
      {tab === "tasks" && <TasksTab d={d} />}
      {tab === "notes" && <NotesTab d={d} />}
      {tab === "files" && <FilesTab d={d} />}
      {tab === "dashboard" && d.can.dashboard && <DashboardTab id={d.team.id} />}
      {tab === "members" && <MembersTab d={d} />}
    </div>
  );
}

/* ── Tasks ── */

function NewTeamTask({ d }: { d: TeamDetail }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [k, setK] = useState(0); // remount the due box after saving so it clears
  const m = useMutation({
    mutationFn: () => post<{ id: string }>("/api/work/items", { spaceId: d.space!.id, title: title.trim(), assigneeIds: assignee ? [assignee] : [], dueAt: due || undefined, priority: "P3" }),
    onSuccess: () => { setTitle(""); setAssignee(""); setDue(""); setK((x) => x + 1); void qc.invalidateQueries({ queryKey: ["team-tasks", d.team.id] }); void qc.invalidateQueries({ queryKey: ["team-dash", d.team.id] }); },
  });
  if (!d.space) return <p className="muted text-sm">This team has no task space yet, so tasks can't be added here.</p>;
  return (
    <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (title.trim()) m.mutate(); }}>
      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_12rem_minmax(0,16rem)_auto]">
        <input className="input" placeholder="New team task…" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Task title" />
        <select className="input" value={assignee} onChange={(e) => setAssignee(e.target.value)} aria-label="Assignee">
          <option value="">No one yet</option>
          {d.members.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <DueInput key={k} value={due || null} onChange={(iso) => setDue(iso ?? "")} />
        <button className="btn btn-primary justify-center" disabled={!title.trim() || m.isPending}><Plus size={15} /> {m.isPending ? "Adding…" : "Add"}</button>
      </div>
      <ErrorNote error={m.error} />
    </form>
  );
}

function TasksTab({ d }: { d: TeamDetail }) {
  const [state, setState] = useState<"open" | "done" | "all">("open");
  const q = useQuery({ queryKey: ["team-tasks", d.team.id, state], queryFn: () => api<{ items: TeamTask[] }>(`/api/teams/${d.team.id}/tasks?state=${state}`) });
  const items = q.data?.items ?? [];
  return (
    <div className="space-y-4">
      {(d.isMember || d.can.manage) && <Card><NewTeamTask d={d} /></Card>}
      <Card title="Team tasks" icon={<ListChecks size={16} />} count={items.length} flush
        actions={
          <div className="flex rounded-lg border border-[var(--line)] p-0.5 text-xs font-semibold">
            {(["open", "done", "all"] as const).map((s) => (
              <button key={s} className={clsx("rounded-md px-2.5 py-1 capitalize", state === s ? "bg-brand-50 text-brand-700" : "text-[var(--muted)]")} onClick={() => setState(s)}>{s}</button>
            ))}
          </div>
        }>
        <ErrorNote error={q.error} />
        {q.isLoading ? <Empty>Loading…</Empty> : items.length ? (
          <ul className="px-2">
            {items.map((t) => (
              <li key={t.id}>
                <Link to="/work/$id" params={{ id: t.id }} className="group block rounded-xl px-3 py-2.5 hover:bg-black/[0.03]">
                  <span className="block truncate text-[0.9375rem] font-semibold group-hover:text-brand-700">{t.title}</span>
                  <span className="muted mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
                    <span className="num font-medium">{t.ref}</span>
                    <StatusPill state={t.state} />
                    <Due at={t.dueAt} />
                    <span className="inline-flex items-center gap-1"><Users size={12} /> {names(t.assigneeNames) || "Unassigned"}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : <Empty>{state === "open" ? "No open tasks for this team." : state === "done" ? "Nothing done yet." : "No tasks yet."}</Empty>}
      </Card>
    </div>
  );
}

/* ── Notes ── */

function NotesTab({ d }: { d: TeamDetail }) {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["notes", "team", d.team.id], queryFn: () => api<{ items: TeamNote[] }>(`/api/notes?${new URLSearchParams({ kind: "note", scope: "all", teamId: d.team.id })}`) });
  const open = (id: string) => void navigate({ href: `/notes?note=${encodeURIComponent(id)}` });
  const create = useMutation({
    mutationFn: () => post<{ id: string }>("/api/notes", { title: "Untitled team note", body: "", teamId: d.team.id }),
    onSuccess: (n) => open(n.id),
  });
  const items = q.data?.items ?? [];
  return (
    <Card title="Team notes" icon={<NotebookPen size={16} />} count={items.length} flush
      actions={d.isMember && <button className="btn btn-sm btn-primary" disabled={create.isPending} onClick={() => create.mutate()}><Plus size={14} /> New team note</button>}>
      <div className="px-5"><ErrorNote error={q.error ?? create.error} /></div>
      {q.isLoading ? <Empty>Loading…</Empty> : items.length ? (
        <ul className="px-2">
          {items.map((n) => (
            <li key={n.id}>
              <button onClick={() => open(n.id)} className="group block w-full rounded-xl px-3 py-2.5 text-left hover:bg-black/[0.03]">
                <span className="block truncate font-semibold group-hover:text-brand-700">{n.title || "Untitled"}</span>
                {n.snippet && <span className="muted mt-0.5 line-clamp-2 block text-xs">{n.snippet}</span>}
                <span className="muted mt-1 block text-[11px]">{[n.ownerName, fmtDate(n.updatedAt)].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : <Empty icon={<NotebookPen size={20} />}>No team notes yet.{d.isMember ? " Start one and everyone in the team can read and edit it." : ""}</Empty>}
    </Card>
  );
}

/* ── Files ── */

function FilesTab({ d }: { d: TeamDetail }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<Array<{ name: string; p: number; error?: string }>>([]);
  const [err, setErr] = useState<unknown>(null);
  const [drag, setDrag] = useState(false);
  const q = useQuery({ queryKey: ["team-docs", d.team.id], queryFn: () => api<{ items: TeamDoc[] }>(`/api/teams/${d.team.id}/documents`) });
  const canAdd = d.isMember || d.can.manage;
  const items = q.data?.items ?? [];

  const add = async (list: File[]) => {
    if (!list.length) return;
    setErr(null);
    setQueue(list.map((f) => ({ name: f.name, p: 0 })));
    const ids: string[] = [];
    await Promise.all(list.map(async (f, i) => {
      try {
        ids.push((await upload(f, (p) => setQueue((qq) => qq.map((x, j) => (j === i ? { ...x, p } : x))))).id);
        setQueue((qq) => qq.map((x, j) => (j === i ? { ...x, p: 1 } : x)));
      } catch (e) {
        setQueue((qq) => qq.map((x, j) => (j === i ? { ...x, error: (e as Error).message } : x)));
      }
    }));
    try {
      // the API takes at most 20 per call
      for (let i = 0; i < ids.length; i += 20) await post(`/api/teams/${d.team.id}/documents`, { documentIds: ids.slice(i, i + 20) });
    } catch (e) { setErr(e); }
    void qc.invalidateQueries({ queryKey: ["team-docs", d.team.id] });
    setQueue((qq) => qq.filter((x) => x.error));
  };

  return (
    <Card title="Team files" icon={<Paperclip size={16} />} count={items.length}
      actions={canAdd && <button className="btn btn-sm" onClick={() => input.current?.click()}><Upload size={14} /> Add files</button>}>
      <div
        onDragOver={(e) => { if (canAdd) { e.preventDefault(); setDrag(true); } }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (canAdd) void add(Array.from(e.dataTransfer.files)); }}
        className={clsx("rounded-xl", drag && "ring-2 ring-brand-400 ring-offset-2")}
      >
        <ErrorNote error={q.error ?? err} />
        {q.isLoading ? <Empty>Loading…</Empty> : items.length > 0 && (
          <ul className="divide-y divide-[var(--line)]">
            {items.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                <FileText size={18} className="muted shrink-0" />
                <a href={`/api/documents/${f.id}/content`} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-medium hover:underline">{f.filename || f.title}</a>
                {f.fusionDocsStatus === "stored" && (f.fusionDocsUrl
                  ? <a href={f.fusionDocsUrl} target="_blank" rel="noreferrer" className="chip !border-emerald-200 !bg-emerald-50 !text-emerald-700 hover:underline">In FusionDocs</a>
                  : <span className="chip !border-emerald-200 !bg-emerald-50 !text-emerald-700">In FusionDocs</span>)}
                <span className="muted shrink-0 text-xs">{[human(f.size), f.addedBy, fmtDate(f.addedAt)].filter(Boolean).join(" · ")}</span>
                <a href={`/api/documents/${f.id}/content?download=1`} className="btn btn-ghost btn-sm !px-1.5" aria-label={`Download ${f.filename}`}><Download size={14} /></a>
              </li>
            ))}
          </ul>
        )}
        {!q.isLoading && !items.length && !queue.length && (
          canAdd
            ? <button className="muted flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-[var(--line)] px-4 py-6 text-sm hover:border-brand-300 hover:text-brand-700" onClick={() => input.current?.click()}><Upload size={18} /> Drop files here or click to choose<span className="text-xs">Several at once · filed to FusionDocs for the team</span></button>
            : <p className="muted text-sm">No files yet.</p>
        )}
        {queue.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {queue.map((x, i) => (
              <li key={i} className="text-xs">
                <span className="flex justify-between gap-2"><span className="truncate">{x.name}</span><span className={x.error ? "text-red-700" : "muted"}>{x.error ?? `${Math.round(x.p * 100)}%`}</span></span>
                {!x.error && <span className="mt-0.5 block h-1 overflow-hidden rounded bg-black/[0.06]"><span className="block h-full bg-brand-500 transition-all" style={{ width: `${x.p * 100}%` }} /></span>}
              </li>
            ))}
            {queue.some((x) => x.error) && <button className="muted text-xs underline" onClick={() => setQueue([])}>Dismiss</button>}
          </ul>
        )}
      </div>
      <input ref={input} type="file" multiple hidden onChange={(e) => { void add(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
    </Card>
  );
}

/* ── Dashboard ── */

function DashboardTab({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["team-dash", id], queryFn: () => api<Dash>(`/api/teams/${id}/dashboard`), refetchInterval: 60_000 });
  if (!q.data) return q.isLoading ? <Empty>Loading…</Empty> : <ErrorNote error={q.error} />;
  const { totals, people, attention } = q.data;
  const cell = "px-3 py-2 text-right num";
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Open" value={totals.open} />
        <Stat label="Overdue" value={totals.overdue} tone={totals.overdue ? "bad" : "default"} />
        <Stat label="Done this week" value={totals.doneThisWeek} tone="good" />
        <Stat label="Unassigned" value={totals.unassigned} tone={totals.unassigned ? "warn" : "default"} />
      </div>
      <Card title="Who has what" icon={<Users size={16} />} flush>
        {people.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="muted border-b border-[var(--line)] text-xs">
                  <th className="px-3 py-2 text-left font-semibold">Person</th>
                  <th className="px-3 py-2 text-right font-semibold">Open</th>
                  <th className="px-3 py-2 text-right font-semibold">Overdue</th>
                  <th className="px-3 py-2 text-right font-semibold">Blocked</th>
                  <th className="px-3 py-2 text-right font-semibold">Waiting</th>
                  <th className="px-3 py-2 text-right font-semibold">Done this week</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {people.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2"><span className="flex items-center gap-2"><Avatar name={p.name} size={24} /><span className="truncate font-medium">{p.name}</span></span></td>
                    <td className={cell}>{p.open}</td>
                    <td className={clsx(cell, p.overdue > 0 && "font-bold text-red-600")}>{p.overdue}</td>
                    <td className={cell}>{p.blocked}</td>
                    <td className={cell}>{p.waiting}</td>
                    <td className={cell}>{p.doneThisWeek}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No one in the team yet.</Empty>}
      </Card>
      <Card title="Needs attention" icon={<AlertTriangle size={16} />} count={attention.length} flush>
        {attention.length ? (
          <ul className="px-2">
            {attention.map((t) => (
              <li key={t.id}>
                <Link to="/work/$id" params={{ id: t.id }} className="group block rounded-xl px-3 py-2.5 hover:bg-black/[0.03]">
                  <span className="block truncate font-semibold group-hover:text-brand-700">{t.title}</span>
                  <span className="muted mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
                    <span className="num font-medium">{t.ref}</span>
                    <StatusPill state={t.state} />
                    <Due at={t.dueAt} />
                    <span className="inline-flex items-center gap-1"><Users size={12} /> {names(t.assigneeNames) || "Unassigned"}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : <Empty>Nothing needs attention.</Empty>}
      </Card>
    </div>
  );
}

/* ── Members ── */

function MembersTab({ d }: { d: TeamDetail }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState<Person[]>([]);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["team", d.team.id] }); void qc.invalidateQueries({ queryKey: ["teams-mine"] }); void qc.invalidateQueries({ queryKey: ["team-dash", d.team.id] }); };
  const set = useMutation({ mutationFn: (v: { personId: string; role: "member" | "lead" }) => put(`/api/teams/${d.team.id}/members/${v.personId}`, { role: v.role }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (personId: string) => del(`/api/teams/${d.team.id}/members/${personId}`), onSuccess: refresh });
  const addAll = useMutation({
    mutationFn: async (people: Person[]) => { for (const p of people) await put(`/api/teams/${d.team.id}/members/${p.id}`, { role: "member" }); },
    onSuccess: () => { setPick([]); refresh(); },
  });
  const fresh = pick.filter((p) => !d.members.some((m) => m.id === p.id));
  const can = useCan();
  const [inviting, setInviting] = useState(false);
  return (
    <div className="space-y-4">
      {d.can.manage && (
        <Card title="Add people">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1"><PersonPicker value={pick} onChange={setPick} placeholder="Search people to add…" /></div>
            <button className="btn btn-primary justify-center" disabled={!fresh.length || addAll.isPending} onClick={() => addAll.mutate(fresh)}><Plus size={15} /> {addAll.isPending ? "Adding…" : "Add to team"}</button>
          </div>
          <div className="mt-2"><ErrorNote error={addAll.error} /></div>
          {(can("directory.person.invite") || can("directory.person.manage")) && (
            <button className="muted mt-2 inline-flex items-center gap-1 text-xs hover:text-[var(--text)] hover:underline" onClick={() => setInviting(true)}><Send size={12} /> Not in ONE yet? Invite them</button>
          )}
          {inviting && <InviteToTeamDialog teamId={d.team.id} teamName={d.team.name} onClose={() => setInviting(false)} />}
        </Card>
      )}
      <Card title="Members" icon={<Users size={16} />} count={d.members.length} flush>
        <div className="px-5"><ErrorNote error={set.error ?? remove.error} /></div>
        {d.members.length ? (
          <ul className="divide-y divide-[var(--line)]">
            {d.members.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5">
                <Avatar name={m.name} size={32} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-semibold">{m.name}</span>
                    {m.lead && <span className="chip !border-amber-200 !bg-amber-50 !text-amber-800"><Crown size={11} /> Lead</span>}
                  </span>
                  {m.email && <span className="muted block truncate text-xs">{m.email}</span>}
                </span>
                {d.can.manage && (
                  <span className="flex shrink-0 gap-1.5">
                    {!m.lead && <button className="btn btn-sm" disabled={set.isPending} onClick={() => confirm(`Make ${m.name} the team lead?`) && set.mutate({ personId: m.id, role: "lead" })}><Crown size={13} /> Make lead</button>}
                    <button className="btn btn-sm btn-danger" disabled={remove.isPending} onClick={() => confirm(`Remove ${m.name} from ${d.team.name}?`) && remove.mutate(m.id)} aria-label={`Remove ${m.name}`}><UserMinus size={13} /> Remove</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : <Empty>No members yet.</Empty>}
      </Card>
    </div>
  );
}
